import { useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import toast from 'react-hot-toast'
import { FaSearch, FaClipboardCheck, FaTruck, FaCarSide, FaClock, FaShieldAlt, FaStar } from 'react-icons/fa'
import PlateSearchConfigurator, { type SearchData } from '@/components/plate-search/PlateSearchConfigurator'
import AvailabilityResults, { type AvailablePlate } from '@/components/plate-search/AvailabilityResults'
import { UmweltplaketteModal, CarbonUpgradeModal } from '@/components/plate-search/UpsellModal'
import { searchPlates } from '@/services/search/plateSearchService'
import { findByRegionCode, type Zulassungsstelle } from '@/services/zulassungsstellenService'
import { useCartStore } from '@/store/cartStore'

export default function Wunschkennzeichen() {
    const navigate = useNavigate()
    const [isSearching, setIsSearching] = useState(false)
    const [searchedPattern, setSearchedPattern] = useState('')
    const [availablePlates, setAvailablePlates] = useState<AvailablePlate[]>([])
    const [error, setError] = useState<string | null>(null)
    const [selectedOffice, setSelectedOffice] = useState<Zulassungsstelle | null>(null)
    const [lastSearchData, setLastSearchData] = useState<SearchData | null>(null)

    // Zulassungsstelle selection modal
    const [officeOptions, setOfficeOptions] = useState<Zulassungsstelle[]>([])
    const [showOfficeModal, setShowOfficeModal] = useState(false)
    const [pendingSearch, setPendingSearch] = useState<SearchData | null>(null)

    // Upsell flow
    const [reservingPlate, setReservingPlate] = useState<AvailablePlate | null>(null)
    const [upsellStep, setUpsellStep] = useState<0 | 1 | 2>(0) // 0=none, 1=umweltplakette, 2=carbon
    const addItem = useCartStore(s => s.addItem)
    const lastLookupCode = useRef('')

    const handleCityCodeChange = useCallback(async (cityCode: string) => {
        const upper = cityCode.toUpperCase()
        if (upper === lastLookupCode.current) return
        lastLookupCode.current = upper

        if (!cityCode || cityCode.length < 1) {
            setSelectedOffice(null)
            setOfficeOptions([])
            return
        }

        try {
            const offices = await findByRegionCode(cityCode)
            setOfficeOptions(offices)
            if (offices.length === 1) {
                setSelectedOffice(offices[0])
            } else if (offices.length === 0) {
                setSelectedOffice(null)
            } else {
                // Multiple offices: clear previous selection if it doesn't match new code
                setSelectedOffice(prev => {
                    if (prev && prev.regionCode.toUpperCase() === upper) return prev
                    return null
                })
            }
            // Multiple offices: wait for user to exit the city code zone (handleCityCodeExit)
        } catch {
            setSelectedOffice(null)
        }
    }, [])

    const handleCityCodeExit = useCallback((cityCode: string) => {
        if (!cityCode || cityCode.length < 1) return
        const upper = cityCode.toUpperCase()
        // Show modal when there are multiple options for THIS code and none picked yet
        const matchesSelected = selectedOffice && selectedOffice.regionCode.toUpperCase() === upper
        if (officeOptions.length > 1 && !matchesSelected) {
            setShowOfficeModal(true)
        }
    }, [officeOptions, selectedOffice])

    const executeSearch = useCallback(async (data: SearchData, officeId: number) => {
        setIsSearching(true)
        setError(null)
        setSearchedPattern(`${data.cityCode} ${data.letters} ${data.numbers}`)
        setAvailablePlates([])
        setLastSearchData(data)

        try {
            const results = await searchPlates({
                cityCode: data.cityCode,
                letters: data.letters || '?',
                numbers: data.numbers || '?',
                vehicleCategory: data.vehicleCategory,
                plateSize: data.plateSize,
                plateType: data.plateType,
                registrationOfficeServiceId: officeId,
                seasonFrom: data.seasonFrom,
                seasonTo: data.seasonTo,
            })

            setAvailablePlates(results.map(r => ({
                city: r.cityCode,
                middle: r.letters,
                end: r.numbers,
            })))
        } catch (err: any) {
            console.error('Search failed:', err)
            setError(err.message || 'Ein unbekannter Fehler ist aufgetreten')
        } finally {
            setIsSearching(false)
        }
    }, [])

    const handleSearch = useCallback(async (data: SearchData) => {
        // If we already have a selected office matching this city code, use it
        if (selectedOffice && selectedOffice.regionCode.toUpperCase() === data.cityCode.toUpperCase()) {
            executeSearch(data, selectedOffice.id)
            return
        }

        // Look up Zulassungsstellen for this city code
        try {
            const offices = await findByRegionCode(data.cityCode)

            if (offices.length === 0) {
                setSearchedPattern(`${data.cityCode} ${data.letters} ${data.numbers}`)
                setError(`Keine Zulassungsstelle für den Stadtcode "${data.cityCode}" gefunden. Bitte prüfen Sie den Stadtcode.`)
                return
            }

            if (offices.length === 1) {
                // Single match - use directly
                setSelectedOffice(offices[0])
                executeSearch(data, offices[0].id)
            } else {
                // Multiple matches - ask user to pick
                setOfficeOptions(offices)
                setPendingSearch(data)
                setShowOfficeModal(true)
            }
        } catch (err: any) {
            setError('Fehler beim Laden der Zulassungsstellen: ' + (err.message || ''))
        }
    }, [selectedOffice, executeSearch])

    const handleOfficeSelect = (office: Zulassungsstelle) => {
        setSelectedOffice(office)
        setShowOfficeModal(false)
        if (pendingSearch) {
            executeSearch(pendingSearch, office.id)
            setPendingSearch(null)
        }
    }

    const handleClear = () => {
        setSearchedPattern('')
        setAvailablePlates([])
        setError(null)
    }

    // ── Upsell flow ──────────────────────────────────────────────────────────

    const plateNumberStr = reservingPlate
        ? `${reservingPlate.city} ${reservingPlate.middle} ${reservingPlate.end}`
        : ''

    const handleReserve = (plate: AvailablePlate) => {
        setReservingPlate(plate)
        // Umweltplakette only applies to cars (PKW) - skip straight to Carbon for others
        const isCar = lastSearchData?.vehicleCategory === 'PKW'
        setUpsellStep(isCar ? 1 : 2)
    }

    const finishReservation = () => {
        if (!reservingPlate) return
        const vc = lastSearchData?.vehicleCategory
        const plateQty = (vc === 'PKW' || vc === 'TRAKTOR' || vc === 'TRUCK') ? 2 : 1
        const plateRef = `${reservingPlate.city}-${reservingPlate.middle}-${reservingPlate.end}`
        addItem({
            productId: `plate-${plateRef}`,
            productName: `Kennzeichen ${reservingPlate.city} ${reservingPlate.middle} ${reservingPlate.end}`,
            price: 20.00,
            quantity: plateQty,
            customization: {
                cityCode: reservingPlate.city,
                letters: reservingPlate.middle,
                numbers: reservingPlate.end,
                plateSize: lastSearchData?.plateSize,
                plateType: lastSearchData?.plateType,
                vehicleCategory: lastSearchData?.vehicleCategory,
                source: 'wunschkennzeichen',
                plateRef,
            },
        })
        toast.success(`Kennzeichen ${plateNumberStr} wurde reserviert!`)
        setReservingPlate(null)
        setUpsellStep(0)
        navigate('/cart')
    }

    const handleUmweltAccept = (emissionClass: 2 | 3 | 4) => {
        if (!reservingPlate) return
        const plateRef = `${reservingPlate.city}-${reservingPlate.middle}-${reservingPlate.end}`
        const colorLabel = emissionClass === 4 ? 'Grün' : emissionClass === 3 ? 'Gelb' : 'Rot'
        addItem({
            productId: `umweltplakette-${plateRef}`,
            productName: `Umweltplakette ${colorLabel} (${plateNumberStr})`,
            price: 5.00,
            quantity: 1,
            customization: { source: 'wunschkennzeichen', plateRef, emissionClass, color: colorLabel },
        })
        toast.success(`Umweltplakette ${colorLabel} hinzugefügt!`)
        setUpsellStep(2)
    }

    const handleUmweltDecline = () => {
        setUpsellStep(2)
    }

    const handleCarbonAccept = () => {
        if (!reservingPlate) return
        const plateRef = `${reservingPlate.city}-${reservingPlate.middle}-${reservingPlate.end}`
        addItem({
            productId: `carbon-upgrade-${plateRef}`,
            productName: `Carbon Upgrade (${plateNumberStr})`,
            price: 4.90,
            quantity: 1,
            customization: { source: 'wunschkennzeichen', plateRef },
        })
        toast.success('Carbon Upgrade hinzugefügt!')
        finishReservation()
    }

    const handleCarbonDecline = () => {
        finishReservation()
    }

    return (
        <div className="min-h-screen bg-dark-950 text-white relative flex flex-col pt-10 sm:pt-16 pb-20">

            {/* Background Decor */}
            <div className="absolute top-0 inset-x-0 h-[50vh] bg-gradient-to-b from-primary-900/20 via-dark-950 to-dark-950 pointer-events-none -z-10"></div>

            <div className="container-custom px-4 sm:px-6 lg:px-8 max-w-5xl z-10 mx-auto w-full">

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-10"
                >
                    <h1 className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white via-primary-200 to-white">
                        Wunschkennzeichen online reservieren
                    </h1>
                    {!selectedOffice && (
                        <p className="text-gray-400 text-lg sm:text-xl max-w-2xl mx-auto">
                            Geben Sie Ihren Stadtcode ein und prüfen Sie die Verfügbarkeit
                        </p>
                    )}
                </motion.div>

                {/* Zulassungsstelle Info Card */}
                {selectedOffice && (
                    <motion.div
                        key={selectedOffice.regionCode}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35 }}
                        className="mb-8 bg-gradient-to-br from-dark-800/90 to-dark-900/90 border border-dark-600/40 rounded-2xl p-5 sm:p-6 flex items-center gap-5 sm:gap-6 shadow-lg shadow-black/20 overflow-hidden relative"
                    >
                        {/* Subtle accent line */}
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-primary-400 to-primary-600 rounded-l-2xl" />

                        <img
                            src={`/wappen/${selectedOffice.regionCode}.webp`}
                            alt={`Wappen ${selectedOffice.name}`}
                            className="w-14 h-14 sm:w-[72px] sm:h-[72px] object-contain flex-shrink-0 drop-shadow-lg ml-1"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                        />

                        <div className="min-w-0">
                            <h2 className="text-base sm:text-lg font-bold text-primary-300 mb-2.5 leading-snug">
                                Recherche bei Zulassungsstelle {selectedOffice.name}
                            </h2>
                            <div className="flex flex-wrap gap-x-6 gap-y-1.5 text-sm">
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-500">Kürzel</span>
                                    <span className="text-white font-bold">{selectedOffice.regionCode}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-500">Bezirk</span>
                                    <span className="text-white font-medium">{selectedOffice.office}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-500">Bundesland</span>
                                    <span className="text-white font-medium">{selectedOffice.bundesland}</span>
                                </div>
                                {selectedOffice.reservationFee > 0 && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-gray-500">Gebühr</span>
                                        <span className="text-amber-400 font-semibold">{selectedOffice.reservationFee.toFixed(2)} EUR</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* Search Configurator Component */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    <PlateSearchConfigurator onSearch={handleSearch} isSearching={isSearching} onCityCodeChange={handleCityCodeChange} onCityCodeExit={handleCityCodeExit} />
                </motion.div>

                {/* Results Component */}
                {(searchedPattern || isSearching || error) && (
                    <AvailabilityResults
                        isLoading={isSearching}
                        searchedPattern={searchedPattern}
                        availablePlates={availablePlates}
                        error={error}
                        onClear={handleClear}
                        onReserve={handleReserve}
                        plateSize={lastSearchData?.plateSize}
                        plateType={lastSearchData?.plateType}
                        vehicleCategory={lastSearchData?.vehicleCategory}
                        seasonFrom={lastSearchData?.seasonFrom}
                        seasonTo={lastSearchData?.seasonTo}
                    />
                )}

                {/* So einfach funktioniert es */}
            <div className="mt-20 relative overflow-hidden rounded-2xl bg-gradient-to-br from-dark-800/90 to-dark-900/90 border border-dark-600/30 p-8 sm:p-10">
                <div className="absolute inset-0 bg-gradient-to-r from-primary-600/10 via-transparent to-primary-600/5 pointer-events-none" />
                <h2 className="relative text-xl sm:text-2xl font-bold text-white mb-10">So einfach funktioniert es</h2>
                <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                    {[
                        { icon: FaSearch, title: 'Wunschkennzeichen', desc: 'Prüfen Sie die Verfügbarkeit Ihrer Wunschkombination' },
                        { icon: FaClipboardCheck, title: 'Reservierung', desc: 'Reservieren Sie Ihr Wunschkennzeichen in nur 1 Minute' },
                        { icon: FaTruck, title: 'Lieferung', desc: 'Wir schicken Ihnen 2 Kennzeichen zum Vorteilspreis und den Reservierungs-PIN zu' },
                        { icon: FaCarSide, title: 'Zulassung', desc: 'Mit Kennzeichen + PIN können Sie Ihr Auto einfach und schnell zulassen' },
                    ].map((step, i) => (
                        <div key={i} className="relative flex flex-col">
                            <div className="flex items-center gap-3 mb-4">
                                <span className="w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 shadow-lg shadow-primary-500/30">
                                    {i + 1}
                                </span>
                                <step.icon className="w-7 h-7 text-primary-400" />
                            </div>
                            <h3 className="text-base font-semibold text-white mb-1.5">{step.title}</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">{step.desc}</p>
                            {i < 3 && <div className="hidden lg:block absolute top-4 -right-4 text-dark-600 text-lg select-none">&rsaquo;</div>}
                        </div>
                    ))}
                </div>
            </div>

            {/* Warum Kennzeichen online reservieren? */}
            <div className="mt-16">
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-8">
                    Warum Kennzeichen online reservieren und bestellen?
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {[
                        { icon: FaClock, title: 'Zeit-Ersparnis', desc: 'Kein doppeltes Anstehen in der Zulassungsbehörde. Sie bringen die Schilder direkt mit und sparen sich den extra Gang zum Schildermacher.' },
                        { icon: FaShieldAlt, title: 'Seriöser Anbieter', desc: 'Bequem von zuhause aus Kennzeichen überprüfen und reservieren. Alle Informationen erhalten Sie direkt per E-Mail, Schilder komfortabel bis zur Haustür.' },
                        { icon: FaStar, title: 'Beste Qualität', desc: 'Unsere Kfz-Schilder werden mit höchsten Produktionsstandards hergestellt. Zudem ist unser Kundenservice rund um die Uhr für Ihre Anliegen verfügbar.' },
                    ].map((item, i) => (
                        <div key={i} className="group bg-dark-800/50 hover:bg-dark-800/80 border border-dark-700/30 hover:border-primary-500/20 rounded-xl p-6 transition-all duration-300">
                            <div className="w-10 h-10 rounded-lg bg-primary-500/10 text-primary-400 flex items-center justify-center mb-4 group-hover:bg-primary-500/20 transition-colors">
                                <item.icon className="w-4.5 h-4.5" />
                            </div>
                            <h3 className="text-base font-semibold text-white mb-2">{item.title}</h3>
                            <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Trust Badges */}
            <div className="mt-14 grid grid-cols-3 gap-4 sm:gap-6">
                {[
                    { label: 'In Deutschland zugelassen', sub: 'Offiziell genehmigt' },
                    { label: 'DIN-zertifiziert', sub: 'Register-Nr. 1M13616/38' },
                    { label: 'Sichere SSL Verbindung', sub: '256-Bit Verschlüsselung' },
                ].map((badge, i) => (
                    <div key={i} className="flex flex-col items-center text-center py-5 px-3 border border-dark-700/30 rounded-xl bg-dark-900/40">
                        <div className="w-12 h-12 rounded-full border border-gray-600/50 bg-dark-800 flex items-center justify-center mb-3">
                            {i === 0 && <img src="/wappen/B.webp" alt="DE" className="w-7 h-7 object-contain" />}
                            {i === 1 && <span className="text-white text-[10px] font-black leading-tight text-center">DIN</span>}
                            {i === 2 && <FaShieldAlt className="w-5 h-5 text-primary-400" />}
                        </div>
                        <span className="text-white text-xs sm:text-sm font-medium">{badge.label}</span>
                        <span className="text-gray-500 text-[10px] sm:text-xs mt-0.5">{badge.sub}</span>
                    </div>
                ))}
                </div>

            </div>

            {/* Zulassungsstelle Selection Modal */}
            {showOfficeModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-dark-900 border border-dark-700 rounded-2xl p-6 max-w-lg w-full shadow-2xl"
                    >
                        <h3 className="text-xl font-bold text-white mb-2">Zulassungsstelle wählen</h3>
                        <p className="text-gray-400 text-sm mb-6">
                            Für den Stadtcode <strong className="text-white">{pendingSearch?.cityCode}</strong> gibt es mehrere Zulassungsstellen:
                        </p>
                        <div className="space-y-3 max-h-80 overflow-y-auto">
                            {officeOptions.map((office) => (
                                <button
                                    key={office.id}
                                    onClick={() => handleOfficeSelect(office)}
                                    className="w-full text-left p-4 bg-dark-800/60 hover:bg-dark-800 border border-dark-700/50 hover:border-primary-500/50 rounded-xl transition-all"
                                >
                                    <div className="font-semibold text-white">{office.name}</div>
                                    <div className="text-sm text-gray-400">{office.office} - {office.bundesland}</div>
                                    {office.reservationFee > 0 && (
                                        <div className="text-xs text-amber-400 mt-1">Reservierungsgebühr: {office.reservationFee.toFixed(2)} EUR</div>
                                    )}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => { setShowOfficeModal(false); setPendingSearch(null) }}
                            className="w-full mt-4 py-3 text-gray-400 hover:text-white text-sm transition-colors"
                        >
                            Abbrechen
                        </button>
                    </motion.div>
                </div>
            )}

            {/* Upsell Modals */}
            {upsellStep === 1 && (
                <UmweltplaketteModal
                    plateNumber={plateNumberStr}
                    onAccept={handleUmweltAccept}
                    onDecline={handleUmweltDecline}
                />
            )}
            {upsellStep === 2 && (
                <CarbonUpgradeModal
                    plateNumber={plateNumberStr}
                    onAccept={handleCarbonAccept}
                    onDecline={handleCarbonDecline}
                    cityCode={reservingPlate?.city}
                    letters={reservingPlate?.middle}
                    numbers={reservingPlate?.end}
                    plateSize={lastSearchData?.plateSize}
                    plateType={lastSearchData?.plateType}
                    vehicleCategory={lastSearchData?.vehicleCategory}
                />
            )}
        </div>
    )
}
